import { Vector3 } from 'three';
import type * as zod from 'zod';

import type { Plane } from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type { WristCamera } from '@sb/integrations/implementations/WristCamera/implementation/WristCamera';
import type {
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import * as log from '@sb/log';
import type { ArmJointPositions } from '@sb/motion-planning';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { Space } from '@sb/routine-runner/types';
import { runLocate } from '@sb/routine-runner/vision/runLocate';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { six } from '@sb/utilities';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

const ns = log.namespace('locateStep');

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

/**
 * We save a dummy set of joint angles for each position. Calculation of the joint angles
 * to use to reach the position should be deferred until a move arm step is played
 */
const NULL_JOINT_ANGLES: ArmJointPositions = six(0);

export default class LocateStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      latestResult: null,
      resultCount: 0,
    };
  }

  private getPlaneSpaceItem(): Space.Plane {
    if (!this.args.planeID) {
      throw new Error('No planeID provided');
    }

    const plane = this.routineContext.getSpaceItem(this.args.planeID);

    if (plane.kind !== 'plane') {
      throw new Error(`SpaceItem ${this.args.planeID} is not a plane`);
    }

    return plane;
  }

  private getPlaneFromSpaceItemID(): Plane {
    const plane = this.getPlaneSpaceItem();

    const planePositions = plane.positions.map(
      (position) =>
        new Vector3(position.pose.x, position.pose.y, position.pose.z),
    );

    return {
      origin: planePositions[0],
      plusX: planePositions[1],
      plusY: planePositions[2],
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      log.info(ns`locate.play`, 'Playing Locate step');

      // Use type assertion since useCameraBotCpp may not be in the feature flag types yet
      const useCameraBotCpp = getFeatureFlagSync('useCppCameraBot', false);

      let cameraEquipment: Camera | WristCamera | undefined;
      let accuracyCalibration: Space.AccuracyCalibrationEntry[] | null = null;
      let intrinsics: CameraIntrinsicsExtended | undefined;
      let extrinsics: ExtrinsicsKinematics | undefined;

      if (useCameraBotCpp) {
        if (!this.args.cameraEquipmentId) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No camera equipment ID provided',
          });
        }

        // Use new Camera implementation from camera-bot
        // If cameraEquipmentId is set, use that specific camera
        cameraEquipment = this.routineContext.equipment.getDeviceById(
          this.args.cameraEquipmentId,
        ) as Camera | undefined;

        if (!cameraEquipment) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment with id ${this.args.cameraEquipmentId} not found`,
          });
        }

        const plane = this.getPlaneSpaceItem();

        const cameraConfig = cameraEquipment.getConfig() as CameraConfiguration;
        const { extrinsicsId } = cameraConfig;
        const { intrinsicsId } = cameraConfig;

        if (extrinsicsId == null || intrinsicsId == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no extrinsics or intrinsics set.`,
          });
        }

        extrinsics = cameraEquipment.getExtrinsics(true);

        intrinsics =
          cameraConfig.intrinsicsData[intrinsicsId].intrinsics ?? undefined;

        if (extrinsics == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no extrinsics set.`,
          });
        }

        if (intrinsics == null) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment ${this.args.cameraEquipmentId} has no intrinsics set.`,
          });
        }

        const accuracyCalibrationFull = Object.values(
          plane.cameraAccuracyCalibrations,
        ).find(
          (calibration) =>
            calibration.equipmentId === this.args.cameraEquipmentId &&
            calibration.extrinsicsId === extrinsicsId,
        );

        if (accuracyCalibrationFull != null) {
          accuracyCalibration = accuracyCalibrationFull.calibrationEntries;
        } else {
          log.info(
            ns`locate.play`,
            "No accuracy calibration found for the selected camera's active extrinsics.",
            {
              cameraEquipmentId: this.args.cameraEquipmentId,
              extrinsicsId,
            },
          );
        }
      } else {
        cameraEquipment =
          this.routineContext.equipment.getWristCamera() ?? undefined;

        accuracyCalibration = this.getPlaneSpaceItem().calibrationEntries;
      }

      const runLocateResults = await runLocate({
        camera: {
          equipment: cameraEquipment,
          integration: this.args.camera,
        },
        regionOfInterest: this.args.regionOfInterest,
        method: this.args.method,
        plane: this.getPlaneFromSpaceItemID(),
        objectHeight: this.args.objectHeight,
        resultsLimit: this.args.filters.resultsLimit,
        transform: this.args.transform,
        accuracyCalibration,
        vision: this.routineContext.vision,
        getState: () => this.routineContext.getRoutineRunnerState(),
        equipment: this.routineContext.equipment,
        intrinsics,
        extrinsics,
      });

      const positions: Space.Position[] = runLocateResults.map((result) => {
        return {
          pose: result.pose,
          jointAngles: NULL_JOINT_ANGLES,
          tcpOption: 'ee-any',
        };
      });

      this.setVariable('latestResult', positions);
      this.setVariable('resultCount', positions.length);

      this.routineContext.setPositionListEntries(
        this.args.positionListID,
        positions,
      );
    } catch (error) {
      log.error(ns`locate.play`, 'Locate failed', error);

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Locate',
        },
        failureReason: `${error.message}`,
        error,
      });
    }
  }
}
